import React, {useEffect, useMemo} from "react";
import {apiClient} from "@src/httpClient/api.ts";
import {listEmployees} from "@src/offline/offline.ts";
import {useSyncStore} from "@src/offline/sync.ts";
import {useSearchParams} from "react-router-dom";
import {useQueryWithOffline} from "@src/httpClient/utils/queryHelpers.ts";
import {EmployeeResponse, useEmployeesStore} from "@src/Store/employeesStore.ts";
import {ErrorLoadingMessage} from "@src/Pages/Views/Common.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {useFilters} from "@src/utils/useFilters.ts";
import {Employee} from "@src/httpClient/__generated__/Api.ts";


export const ManagerLayer: React.FC<React.PropsWithChildren> = ({children}) => {
    const [params] = useSearchParams()
    const {employee} = useCredentialsStore()
    const setEmployees = useEmployeesStore(x => x.setEmployees);
    const syncEmployee = useSyncStore(x => x.syncEmployee);
    const addRefetchAction = useEmployeesStore(x => x.addRefetchAction)
    const {appliedFilters, filterArray} = useFilters()

    const searchStr = useMemo(() => {
        return params.get("search") ?? undefined
    }, [params]);

    const {data: employees, isLoading: isEmployeeLoading, error: employeeError, refetch: employeeRefetch} = useQueryWithOffline<EmployeeResponse>(
        ['employees', searchStr],
        // ONLINE
        async (_, signal) => {
            // query params only reach the wire via the raw `search` string (http-client-base ignores `query`)
            const listParams = new URLSearchParams()
            listParams.set('page_size', '200')
            if (searchStr) listParams.set('search', searchStr)
            const result = await apiClient.api.companyEmployeesList(
                undefined, {search: listParams.toString(), signal}
            )
            const items = result.data.results ?? []
            syncEmployee(items)
            return {results: items, count: result.data.count, __source: 'online' as const}
        },
        // OFFLINE
        async (_search, _signal) => {
            const offlineSearch = searchStr ? `search=${searchStr}` : ''
            return await listEmployees(offlineSearch)
        },
        (data) => {
            const self = data.results.find(e => e.id === employee?.id)
            if (self) {
                data.results = [
                    self,
                    ...data.results.filter(e => e.id !== self.id)
                ]
            }
            return data
        },
        false
    );

    useEffect(() => {
        if (employeeRefetch) addRefetchAction(employeeRefetch);
    }, [employeeRefetch]);

    useEffect(() => {
        if (!employees || !employees?.results) return;
        setEmployees(filterArray<Employee>(employees?.results));
    }, [employees, appliedFilters]);

    if (employeeError && !isEmployeeLoading) return <ErrorLoadingMessage/>
    return children
}